import { API } from 'aws-amplify';
import { getAuthToken, checkUserGroup, USER_GROUPS } from '../utils/AuthUtils';
import { getAvailableECSStatuses, DATA_PROVIDER_ALLOWED_STATUSES } from '../utils/ECSUtils';

const API_ENDPOINTS = {
    ANNUAL_COMPENSATIONS: 'AnnualCompensations',
    UNRESTRICTED_SURVEY: 'UnrestrictedSurveyData'
};

const API_PATHS = {
    QUESTIONNAIRE: (surveyYear, companyId, participantId) =>
        `/survey-questionnaire-submission/records/${surveyYear}/${companyId}/${participantId}`,
    PARTICIPANT_METADATA: (participantId) =>
        `/unrestricted/survey/participants?id=${participantId}`,
    LATEST_SUBMISSION: (participantId) =>
        `/restricted/survey/${participantId}/latest-submission`
};

const DEFAULT_HEADERS = {
    'Content-Type': 'application/json'
};

/**
 * Fetches questionnaire record from the API
 * @param {string} surveyYear - The survey year
 * @param {string} companyId - The company ID
 * @param {string} participantId - The participant ID
 * @param {string} authToken - The authentication token
 * @returns {Promise<Object>} The questionnaire response
 */
async function fetchQuestionnaireRecord(surveyYear, companyId, participantId, authToken) {
    const path = API_PATHS.QUESTIONNAIRE(surveyYear, companyId, participantId);
    return API.get(API_ENDPOINTS.ANNUAL_COMPENSATIONS, path, {
        headers: {
            ...DEFAULT_HEADERS,
            'Token': authToken
        }
    });
}

/**
 * Fetches participant metadata from the API
 * @param {string} participantId - The participant ID
 * @param {string} authToken - The authentication token
 * @returns {Promise<Object>} The metadata response
 */
async function fetchParticipantMetadata(participantId, authToken) {
    const path = API_PATHS.PARTICIPANT_METADATA(participantId);
    return API.get(API_ENDPOINTS.UNRESTRICTED_SURVEY, path, {
        headers: {
            'Token': authToken
        }
    });
}

/**
 * Fetches latest submission record from the API
 * @param {string} participantId - The participant ID
 * @param {string} authToken - The authentication token
 * @returns {Promise<Object>} The submission response
 */
async function fetchLatestSubmission(participantId, authToken) {
    const path = API_PATHS.LATEST_SUBMISSION(participantId);
    return API.get(API_ENDPOINTS.ANNUAL_COMPENSATIONS, path, {
        headers: {
            'Token': authToken
        }
    });
}

/**
 * Validates the required parameters
 * @param {Object} params - The parameters to validate
 * @throws {Response} If validation fails
 */
function validateParams(params) {
    const { companyId, participantId } = params;
    if (!companyId || !participantId) {
        throw new Response("Missing required parameters: companyId and participantId are required", { status: 400 });
    }
}

/**
 * Loader function for questionnaire details
 * @param {Object} params - The route parameters
 * @param {Request} request - The request object
 * @returns {Promise<Object>} The loaded data
 */
export async function questionnaireDetailsLoader({ params, request }) {
    try {
        validateParams(params);

        const { companyId, participantId } = params;
        const url = new URL(request.url);
        const surveyYear = url.searchParams.get('surveyYear') || new Date().getFullYear().toString();
        const processingStatus = url.searchParams.get('status');
        const sensitive = url.searchParams.get('sensitive');

        const authToken = await getAuthToken();
        const isAdmin = await checkUserGroup(USER_GROUPS.ADMIN);

        const questionnaireResponse = await fetchQuestionnaireRecord(surveyYear, companyId, participantId, authToken);

        if (!isAdmin && !DATA_PROVIDER_ALLOWED_STATUSES.includes(questionnaireResponse.processingStatus)) {
            throw new Response("You are not authorized to view this record", { status: 403 });
        }

        const metadataResponse = await fetchParticipantMetadata(participantId, authToken);

        const availableStatuses = await getAvailableECSStatuses();

        return {
            trackingRecord: questionnaireResponse,
            participantMetadata: metadataResponse,
            submissionRecord: isAdmin ? await fetchLatestSubmission(participantId, authToken) : null,
            availableStatuses,
            isAdmin,
            processingStatus,
            sensitive,
            surveyYear,
            participantId
        };
    } catch (error) {
        console.error('Loader error:', error);
        if (error instanceof Response) {
            throw error;
        }
        throw new Response("Failed to load questionnaire details", { status: 500 });
    }
}